import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { logger } from '@/shared/observability/logger';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * The last thing between a thrown render and a blank page.
 *
 * <h2>Why this is still a class</h2>
 *
 * React has no hook equivalent. `componentDidCatch` and
 * `getDerivedStateFromError` exist only on class components, and that is not a
 * gap waiting to be filled — it is the one remaining reason to write one.
 *
 * <h2>What it does not catch</h2>
 *
 * Errors in event handlers, in `setTimeout`, and in promises. Those never reach
 * a boundary, which is why request failures are handled by the query layer
 * instead of being left to bubble here.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Through the logger, and the objection to that has been removed rather than
    // waived. This used console.error deliberately, reasoning that a reporting call
    // which throws during a crash replaces a readable stack trace with an unrelated
    // one. The reasoning was right; the remedy is a logger that cannot throw --
    // every write is wrapped and falls back to the bare console -- rather than a
    // rule with a hole in it.
    logger.error('Unhandled render error', { error, componentStack: info.componentStack });
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-full items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold text-[var(--color-ink)]">Something went wrong</h1>
          <p className="mt-3 text-[var(--color-ink-soft)]">
            The page could not be displayed. Reloading usually fixes it.
          </p>
          <button
            type="button"
            onClick={() => {
              window.location.reload();
            }}
            className="mt-6 rounded-lg bg-[var(--color-accent)] px-5 py-2.5 font-semibold text-white"
          >
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}
